import { Select } from 'src/design/atoms/Select';
import React from 'react';
import { UserDto } from 'src/api/FiretrolApiClient';
import { useTimesheetUsersForOffice } from 'src/api/queries/timesheets/useTimesheetUsersForOffice';

export interface ITimesheetUserSelectProps {
  officeId?: number | null;
  onlyMyTeam?: boolean;
  value: number | null;
  onChange: (userId: number | null) => void;
  placeholder?: string;
  withAsterisk?: boolean;
}

/**
 * Employee picker for timesheet-report workflows. Returns only users whose
 * primary office matches — this is the authoritative filter for timesheet
 * administration, ensuring each user is shown under a single office rather
 * than in every office they belong to.
 */
const EMPTY_USERS: UserDto[] = [];

export const TimesheetUserSelect: React.FC<ITimesheetUserSelectProps> = ({
  officeId,
  onlyMyTeam,
  value,
  onChange,
  placeholder,
}) => {
  const { data: users = EMPTY_USERS, isLoading } = useTimesheetUsersForOffice(
    officeId,
    onlyMyTeam ?? false,
  );

  const data = React.useMemo(
    () =>
      users.map((u) => ({
        value: String(u.id),
        label: `${u.firstName} ${u.lastName}`,
      })),
    [users],
  );

  const resolvedPlaceholder =
    placeholder ??
    (!officeId
      ? 'Select an office first...'
      : isLoading
      ? 'Loading employees...'
      : 'Select an employee...');

  return (
    <Select
      placeholder={resolvedPlaceholder}
      data={data}
      value={value ? String(value) : null}
      onChange={(v) => onChange(v ? Number(v) : null)}
      disabled={!officeId || isLoading}
      searchable
      clearable
    />
  );
};
